import clsx from "clsx";
import type {
  ButtonHTMLAttributes,
  CSSProperties,
  HTMLAttributes,
  ReactNode,
} from "react";

import { renderCoatColors } from "@renderer/coat-palette.js";

import type { HorseSummary, PackHorse } from "@/api";

import { ContentSkeleton, type SkeletonVariant } from "./skeletons";

export function Panel({
  className,
  children,
  ...props
}: HTMLAttributes<HTMLDivElement>) {
  return (
    <section className={clsx("panel", className)} {...props}>
      {children}
    </section>
  );
}

export function Button({
  className,
  variant = "primary",
  ...props
}: ButtonHTMLAttributes<HTMLButtonElement> & {
  variant?: "primary" | "secondary" | "ghost" | "danger";
}) {
  return (
    <button
      className={clsx("button", `button--${variant}`, className)}
      {...props}
    />
  );
}

export function StatusBadge({
  status,
  children,
}: {
  status: string;
  children?: ReactNode;
}) {
  return (
    <span className={clsx("status-badge", `status-badge--${status}`)}>
      <span className="status-badge__dot" aria-hidden />
      {children ?? status.replaceAll("_", " ")}
    </span>
  );
}

export function SectionHeading({
  icon,
  title,
  action,
}: {
  icon?: ReactNode;
  title: string;
  action?: ReactNode;
}) {
  return (
    <div className="section-heading">
      <h2>
        {icon}
        {title}
      </h2>
      {action}
    </div>
  );
}

export function EmptyState({
  title,
  detail,
  action,
}: {
  title: string;
  detail?: string;
  action?: ReactNode;
}) {
  return (
    <div className="empty-state">
      <strong>{title}</strong>
      {detail ? <p>{detail}</p> : null}
      {action}
    </div>
  );
}

export function ErrorState({
  error,
  retry,
}: {
  error: unknown;
  retry?: () => void;
}) {
  const message = error instanceof Error ? error.message : "Request failed.";
  return (
    <div className="error-state" role="alert">
      <strong>Unable to load this surface</strong>
      <p>{message}</p>
      {retry ? (
        <Button variant="secondary" onClick={retry}>
          Retry
        </Button>
      ) : null}
    </div>
  );
}

export function Skeleton({
  rows = 3,
  variant = "rows",
}: {
  rows?: number;
  variant?: SkeletonVariant;
}) {
  return <ContentSkeleton rows={rows} variant={variant} />;
}

type PortraitHorse = (
  | Pick<
      HorseSummary,
      "name" | "tier" | "appearance" | "starRating" | "bloodline"
    >
  | (Pick<PackHorse, "name" | "tier" | "appearance"> & {
      starRating?: string;
      bloodline?: string | number;
    })
) & {
  // Stable identity keeps the portrait tint identical to the 3D coat
  // (coat-palette.js hashes horseId first, name as legacy fallback).
  horseId?: string;
};

export function HorsePortrait({
  horse,
  compact = false,
}: {
  horse: PortraitHorse;
  compact?: boolean;
}) {
  // Same render-layer mapping as the 3D horses: legacy placeholder coats
  // upgrade to the v2 variety palette, custom appearances pass through.
  const { coat, mane } = renderCoatColors(horse);
  const mirrored = Number(horse.bloodline ?? 0) % 2 === 1;
  return (
    <div
      className={clsx("horse-portrait", compact && "horse-portrait--compact")}
      style={
        {
          "--horse-coat": coat,
          "--horse-mane": mane,
          "--horse-direction": mirrored ? -1 : 1,
        } as CSSProperties
      }
      role="img"
      aria-label={`${horse.name}, ${horse.tier} racehorse`}
    >
      <div className="horse-portrait__zed" aria-hidden>
        <img
          className="horse-portrait__zed-base"
          alt=""
          src="/app/static/assets/zed/textures/Horse_F.webp"
        />
        <span className="horse-portrait__zed-coat" />
        <img
          className="horse-portrait__zed-bridle"
          alt=""
          src="/app/static/assets/zed/textures/Horse_F_Bridle.webp"
        />
      </div>
    </div>
  );
}

export function TierStars({
  tier,
  rating,
}: {
  tier: string;
  rating?: string | number;
}) {
  const parsed = Number(rating ?? tierStars(tier));
  const numeric = Number.isFinite(parsed)
    ? Math.min(5, Math.max(0, parsed))
    : 0;
  const full = Math.floor(numeric);
  const half = numeric - full >= 0.5;
  return (
    <span
      aria-label={`${numeric} out of 5 stars`}
      className={clsx("tier-stars", `tier-stars--${tier}`)}
      role="img"
    >
      {Array.from({ length: 5 }, (_, index) => {
        const state =
          index < full ? "full" : index === full && half ? "half" : "empty";
        return (
          <span
            aria-hidden="true"
            className={`tier-star tier-star--${state}`}
            key={index}
          >
            ★
          </span>
        );
      })}
    </span>
  );
}

export function tierColor(tier: string) {
  return (
    {
      common: "#6fa8dc",
      uncommon: "#46c87c",
      rare: "#48a9ff",
      epic: "#b875ff",
      legendary: "#ffcb45",
    }[tier] ?? "#7f91a5"
  );
}

function tierStars(tier: string) {
  return (
    {
      common: 2.5,
      uncommon: 3,
      rare: 3.5,
      epic: 4.5,
      legendary: 5,
    }[tier] ?? 2.5
  );
}

export function money(cents: number | null | undefined, currency = "USD") {
  if (cents === null || cents === undefined) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency,
    minimumFractionDigits: 2,
  }).format(cents / 100);
}

export function odds(oddsH: number | null | undefined) {
  if (!oddsH) return "—";
  return (oddsH / 100).toFixed(2);
}

export function countdown(iso: string, nowMs = Date.now()) {
  const milliseconds = Date.parse(iso) - nowMs;
  if (milliseconds <= 0) return "now";
  const seconds = Math.floor(milliseconds / 1000);
  if (seconds < 60) return `${seconds}s`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ${seconds % 60}s`;
  const hours = Math.floor(minutes / 60);
  return `${hours}h ${minutes % 60}m`;
}
